import React, { useState, useMemo } from 'react';
import { FieldProps } from 'react-final-form';
import { isEqual, sortedUniq, uniq } from 'lodash';

import {
  AdditionalButtonProps,
  Props as ValueBadgeInputProps,
  ValueBadgeInput,
} from '@occc-app/design-system';

export type ValuesPreprocessorResult = {
  variants: string[];
  errors: string[];
};

export type ValuesPreprocessor = (values: string[]) => ValuesPreprocessorResult;

export type ValueBadgeFieldProps = Omit<
  ValueBadgeInputProps,
  'addedValues' | 'onAdd' | 'onRemoveAllValues' | 'onValueRemoved'
> & {
  name: string;
  fieldProps?: Partial<FieldProps<string[], any>>;
  valuesPreprocessor?: ValuesPreprocessor;
  additionalButton?: React.ComponentType<AdditionalButtonProps>;
};

type IValueBadgeInputWrapperProps = Omit<
  ValueBadgeInputProps,
  'addedValues' | 'onAdd' | 'onRemoveAllValues' | 'onValueRemoved'
> & {
  values: string[];
  onChange: (values: string[]) => void;
  parse?: (value: string) => any;
  valuesPreprocessor?: ValuesPreprocessor;
  additionalButton?: React.ComponentType<AdditionalButtonProps>;
  onInputChange?: () => void;
};

export const ValueBadgeInputWrapper = ({
  values,
  onChange,
  parse,
  valuesPreprocessor,
  additionalButton: AdditionalButton,
  onInputChange,
  ...rest
}: IValueBadgeInputWrapperProps) => {
  const [validationError, setValidationError] = useState<string | undefined>();

  const handleInputChange = React.useCallback(() => {
    setValidationError(undefined);
    onInputChange?.();
  }, [onInputChange]);

  const addValue = React.useCallback(
    (value: string, usePreprocessor = false) => {
      const newValues = value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (newValues.length === 0) {
        return;
      }

      if (usePreprocessor && valuesPreprocessor) {
        const result = valuesPreprocessor(newValues);

        if (result.errors.length > 0) {
          setValidationError(result.errors[0]);
          return;
        }

        setValidationError(undefined);
        const sortedUpdatedUniqValues = sortedUniq(uniq([...values, ...result.variants]));

        if (!isEqual(values, sortedUpdatedUniqValues)) {
          onChange(sortedUpdatedUniqValues);
        }
      } else {
        setValidationError(undefined);
        const sortedUpdatedUniqValues = sortedUniq(uniq([...values, ...newValues]));

        if (!isEqual(values, sortedUpdatedUniqValues)) {
          onChange(sortedUpdatedUniqValues);
        }
      }
    },
    [onChange, values, valuesPreprocessor],
  );

  const onValueRemoved = React.useCallback(
    (value: string) => {
      onChange(values.filter((v) => v !== value));
    },
    [onChange, values],
  );

  const onRemoveAllValues = React.useCallback(() => {
    onChange([]);
  }, [onChange]);

  const helpText = validationError !== undefined ? validationError : rest.helpText;
  const isError = validationError !== undefined ? true : rest.isError;

  const AdditionalButtonWithPreprocessor = useMemo(() => {
    if (!AdditionalButton) {
      return undefined;
    }

    return ({ value }: AdditionalButtonProps) => {
      const handleGenerateAndAdd = () => {
        if (!value.length) {
          return;
        }
        // Value is already parsed by FormInputWithButtonRow, use it directly
        addValue(value, true);
      };

      return <AdditionalButton value={value} handleAddValue={handleGenerateAndAdd} />;
    };
  }, [AdditionalButton, addValue]);

  return (
    <ValueBadgeInput
      addedValues={values}
      onAdd={(value) => addValue(value, false)}
      onRemoveAllValues={onRemoveAllValues}
      onValueRemoved={onValueRemoved}
      parse={parse}
      onInputChange={handleInputChange}
      additionalButton={AdditionalButtonWithPreprocessor}
      {...rest}
      helpText={helpText}
      isError={isError}
    />
  );
};
